import { AccountConfig, Website } from '@betterfunnel/types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { DashboardContext } from '../../contexts/DashboardContext';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import {
  WebsiteUpdate,
  WebsiteUpdaterContext,
} from '../../contexts/WebsiteUpdaterContext';
import AlertModal from '../Common/AlertModal';
import Button from '../Common/Button';
import SettingSectionCodeArea from '../settings/SettingSectionCodeArea';
import SettingSectionTextField from '../settings/SettingSectionTextField';
import SettingSectionTitle from '../settings/SettingSectionTitle';

interface Props {
  websiteId: string;
  website?: Website;
  onUpdateWebsiteEvent: (_: WebsiteUpdate) => void;
}

export default function ManageWebsiteConfig({
  websiteId,
  website,
  onUpdateWebsiteEvent,
}: Props) {
  const { show } = useContext(SnackbarContext);
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedDynamicPage, accountConfig } = useContext(DashboardContext);
  const { createWebsiteUpdate } = useContext(WebsiteUpdaterContext);

  const [showResetModal, setShowResetModal] = useState<{
    type: 'account' | 'dynamicPage';
  }>();

  const [config, setConfig] = useState<AccountConfig | undefined>(
    website?.development.config,
  );

  useEffect(() => {
    const config = website?.development.config;

    setConfig({
      ...accountConfig,
      ...selectedDynamicPage?.data().config,
      ...config,
    });
  }, [accountConfig, selectedDynamicPage, website?.development.config]);

  const updateWebsite = useCallback(
    async (data: AccountConfig, reset = false) => {
      if (!selectedDynamicPage) return;
      try {
        const wu = createWebsiteUpdate(
          selectedDynamicPage.id,
          websiteId,
          '',
          '',
          data,
          reset ? 'resetConfig' : 'editConfig',
          [],
          website?.development
            ? JSON.parse(JSON.stringify(website?.development))
            : undefined,
        );
        if (!wu) return;

        onUpdateWebsiteEvent(wu);
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [
      createWebsiteUpdate,
      onUpdateWebsiteEvent,
      selectedDynamicPage,
      show,
      website?.development,
      websiteId,
    ],
  );

  return (
    <div>
      <SettingSectionTitle
        title="Custom code"
        description="Add custom code to all of your landing pages."
      />

      <dl className="mt-6 space-y-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
        <SettingSectionCodeArea
          title="Head"
          value={config?.headCustomCode ?? ''}
          setValue={(value: any) => {
            setConfig({ ...config, headCustomCode: value });
          }}
          update={() => updateWebsite(config ?? {})}
          editable={true}
        />
        {isSupportAdmin && (
          <SettingSectionTextField
            title="Head Delay"
            value={(config?.headCustomCodeDelay ?? 0) + ''}
            setValue={(value: any) => {
              setConfig({
                ...config,
                headCustomCodeDelay: Number.isNaN(+value) ? 0 : +value,
              });
            }}
            update={() => updateWebsite(config ?? {})}
            pattern="^\d*$"
            editable
          />
        )}
        <SettingSectionCodeArea
          title="Body"
          value={config?.bodyCustomCode ?? ''}
          setValue={(value: any) => {
            setConfig({ ...config, bodyCustomCode: value });
          }}
          update={() => updateWebsite(config ?? {})}
          editable={true}
        />
        {isSupportAdmin && (
          <SettingSectionTextField
            title="Body Delay"
            value={(config?.bodyCustomCodeDelay ?? 0) + ''}
            setValue={(value: any) => {
              setConfig({
                ...config,
                bodyCustomCodeDelay: Number.isNaN(+value) ? 0 : +value,
              });
            }}
            update={() => updateWebsite(config ?? {})}
            pattern="^\d*$"
            editable
          />
        )}
      </dl>

      <div className="pb-10 pt-16">
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Reset
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-500">
          Reset config to default.
        </p>

        <dl className="mb-3 mt-6 flex flex-col items-start justify-start gap-2 border-t border-gray-200 pt-4 text-sm leading-6">
          <Button
            variant="ternary"
            color="primary"
            onClick={() => setShowResetModal({ type: 'account' })}
          >
            Reset to Account Defaults
          </Button>

          <Button
            variant="ternary"
            color="primary"
            onClick={() => setShowResetModal({ type: 'dynamicPage' })}
          >
            Reset to Dynamic Page Defaults
          </Button>
        </dl>
      </div>

      <AlertModal
        title={`Reset to ${showResetModal?.type === 'account' ? 'Account' : 'Dynamic Page'} Defaults`}
        open={!!showResetModal}
        onClose={() => setShowResetModal(undefined)}
        actions={[
          {
            type: 'secondary',
            title: 'Cancel',
            onClick: () => setShowResetModal(undefined),
          },
          {
            type: 'primary',
            color: 'negative',
            title: 'Reset',
            onClick: () => {
              if (showResetModal?.type === 'account') {
                updateWebsite(accountConfig ?? {}, true);
              } else {
                updateWebsite({}, true);
              }
              setShowResetModal(undefined);
            },
          },
        ]}
        type={'warn'}
      />
    </div>
  );
}
